import React, { useState, useEffect } from 'react';
import './BusinessDailyLogViewer.css';

const BusinessDailyLogViewer = ({ parentCsvUrl, childCsvUrl, visitExperienceCsvUrl, outingScheduleCsvUrl }) => {
  const [parentData, setParentData] = useState([]);
  const [childData, setChildData] = useState([]);
  const [visitExperienceData, setVisitExperienceData] = useState([]);
  const [outingScheduleData, setOutingScheduleData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 親データの取得
        const parentResponse = await fetch(parentCsvUrl);
        if (!parentResponse.ok) {
          throw new Error(`親データの取得に失敗しました: ${parentResponse.status}`);
        }
        const parentCsvText = await parentResponse.text();
        const parentLines = parentCsvText.split('\n').filter(line => line.trim() !== '');
        
                 if (parentLines.length > 0) {
           const parentHeaders = parentLines[0].split(',').map(header => header.trim());
           const parentDataArray = parentLines.slice(1).map(line => {
             // CSVの各行を正しくパースする（カンマで分割するが、引用符内のカンマは無視）
             const values = [];
             let current = '';
             let inQuotes = false;
             
             for (let i = 0; i < line.length; i++) {
               const char = line[i];
               if (char === '"') {
                 inQuotes = !inQuotes;
               } else if (char === ',' && !inQuotes) {
                 values.push(current.trim());
                 current = '';
               } else {
                 current += char;
               }
             }
             values.push(current.trim()); // 最後の値
             
             const row = {};
             parentHeaders.forEach((header, index) => {
               row[header] = values[index] || '';
             });
             return row;
           });
          
                     setHeaders(parentHeaders);
           setParentData(parentDataArray);
           setFilteredData(parentDataArray);
           
           // デバッグ: 日付データの確認
           console.log('Loaded data sample:', parentDataArray.slice(0, 3));
           console.log('Date fields sample:', parentDataArray.slice(0, 3).map(row => row['日付']));
        }

        // 子データの取得（面談スケジュール）
        if (childCsvUrl) {
          const childResponse = await fetch(childCsvUrl);
          if (childResponse.ok) {
            const childCsvText = await childResponse.text();
            const childLines = childCsvText.split('\n').filter(line => line.trim() !== '');
            
            if (childLines.length > 0) {
              const childHeaders = childLines[0].split(',').map(header => header.trim());
              const childDataArray = childLines.slice(1).map(line => {
                const values = line.split(',').map(value => value.trim());
                const row = {};
                childHeaders.forEach((header, index) => {
                  row[header] = values[index] || '';
                });
                return row;
              });
              
              setChildData(childDataArray);
            }
          }
        }

        // 見学体験データの取得
        if (visitExperienceCsvUrl) {
          const visitResponse = await fetch(visitExperienceCsvUrl);
          if (visitResponse.ok) {
            const visitCsvText = await visitResponse.text();
            const visitLines = visitCsvText.split('\n').filter(line => line.trim() !== '');
            
            if (visitLines.length > 0) {
              const visitHeaders = visitLines[0].split(',').map(header => header.trim());
              const visitDataArray = visitLines.slice(1).map(line => {
                const values = line.split(',').map(value => value.trim());
                const row = {};
                visitHeaders.forEach((header, index) => {
                  row[header] = values[index] || '';
                });
                return row;
              });
              
              setVisitExperienceData(visitDataArray);
            }
          }
        }

        // 外出予定データの取得
        if (outingScheduleCsvUrl) {
          const outingResponse = await fetch(outingScheduleCsvUrl);
          if (outingResponse.ok) {
            const outingCsvText = await outingResponse.text();
            const outingLines = outingCsvText.split('\n').filter(line => line.trim() !== '');
            
            if (outingLines.length > 0) {
              const outingHeaders = outingLines[0].split(',').map(header => header.trim());
              const outingDataArray = outingLines.slice(1).map(line => {
                const values = line.split(',').map(value => value.trim());
                const row = {};
                outingHeaders.forEach((header, index) => {
                  row[header] = values[index] || '';
                });
                return row;
              });
              
              setOutingScheduleData(outingDataArray);
            }
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (parentCsvUrl) {
      fetchData();
    }
  }, [parentCsvUrl, childCsvUrl, visitExperienceCsvUrl, outingScheduleCsvUrl]);

  const isValidDate = (dateStr) => {
    if (!dateStr || dateStr === '') return false;
    
    // 日付文字列の形式をチェック（YYYY-MM-DD形式）
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateStr)) {
      console.log('Invalid date format:', dateStr);
      return false;
    }
    
    const date = new Date(dateStr);
    const isValid = !isNaN(date.getTime());
    
    if (!isValid) {
      console.log('Invalid date value:', dateStr);
    }
    
    return isValid;
  };

  useEffect(() => {
    let filtered = parentData;
    
    // 無効な日付のデータを除外
    filtered = filtered.filter(row => isValidDate(row['日付']));
    
    if (selectedYear && selectedMonth) {
      filtered = filtered.filter(row => {
        const date = new Date(row['日付']);
        return date.getFullYear() === parseInt(selectedYear) && 
               (date.getMonth() + 1) === parseInt(selectedMonth);
      });
    } else if (selectedYear) {
      filtered = filtered.filter(row => {
        const date = new Date(row['日付']);
        return date.getFullYear() === parseInt(selectedYear);
      });
    }
    
    setFilteredData(filtered);
  }, [selectedYear, selectedMonth, parentData]);

  const getAvailableYears = () => {
    const years = parentData
      .map(row => row['日付'])
      .filter(date => date && isValidDate(date))
      .map(date => new Date(date).getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  };

  const getAvailableMonths = (year) => {
    if (!year) return [];
    const months = parentData
      .map(row => row['日付'])
      .filter(date => date && isValidDate(date))
      .filter(date => new Date(date).getFullYear() === parseInt(year))
      .map(date => new Date(date).getMonth() + 1);
    return [...new Set(months)].sort((a, b) => a - b);
  };

  const getRelatedChildData = (date) => {
    return childData.filter(row => row['日付'] === date);
  };

  const getRelatedVisitExperienceData = (date) => {
    return visitExperienceData.filter(row => row['日付'] === date);
  };

  const getRelatedOutingScheduleData = (date) => {
    return outingScheduleData.filter(row => row['日付'] === date);
  };

  const handleRowClick = (rowData) => {
    setSelectedRowData(rowData);
    setShowDetail(true);
  };

  const handleCloseDetail = () => {
    setShowDetail(false);
    setSelectedRowData(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === '') return '未設定';
    if (!isValidDate(dateStr)) return '無効な日付';
    
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short'
    });
  };

  const getTotalUsers = (row) => {
    const aType = parseInt(row['本日利用者A型'] || '0');
    const bType = parseInt(row['本日利用者B型'] || '0');
    const transition = parseInt(row['本日利用者移行'] || '0');
    return aType + bType + transition;
  };

  const getTotalInterviews = (row) => {
    const aType = parseInt(row['本日新規面談A型'] || '0');
    const bType = parseInt(row['本日新規面談B型'] || '0');
    const transition = parseInt(row['本日新規面談移行'] || '0');
    return aType + bType + transition;
  };



  if (loading) {
    return <div className="business-daily-log-loading">業務日報データを読み込み中...</div>;
  }

  if (error) {
    return <div className="business-daily-log-error">エラー: {error}</div>;
  }

  return (
    <div className="business-daily-log-viewer">
      <div className="log-header">
        <h2>📅 業務日報ログ</h2>
        <div className="filter-controls">
          <div className="filter-group">
            <label htmlFor="year-select">年:</label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setSelectedMonth(''); // 年が変わったら月をリセット
              }}
            >
              <option value="">全期間</option>
              {getAvailableYears().map((year, index) => (
                <option key={index} value={year}>{year}年</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="month-select">月:</label>
            <select
              id="month-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              disabled={!selectedYear}
            >
              <option value="">全月</option>
              {getAvailableMonths(selectedYear).map((month, index) => (
                <option key={index} value={month}>{month}月</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="summary-dashboard">
        <div className="summary-grid">
          <div className="summary-item">
            <h3>総日数</h3>
            <span className="count">{filteredData.length}</span>
          </div>
          <div className="summary-item">
            <h3>総利用者数</h3>
            <span className="count total-users">
              {filteredData.reduce((sum, row) => sum + getTotalUsers(row), 0)}
            </span>
          </div>
          <div className="summary-item">
            <h3>総面談数</h3>
            <span className="count total-interviews">
              {filteredData.reduce((sum, row) => sum + getTotalInterviews(row), 0)}
            </span>
          </div>
          
        </div>
      </div>

      <div className="business-log-content">
        {filteredData.map((row, index) => (
          <div 
            key={index} 
            className="business-log-card"
            onClick={() => handleRowClick(row)}
          >
            <div className="card-header">
              <div className="date-info">
                <h3 className="date-title">{formatDate(row['日付'])}</h3>
                <div className="date-badge">
                  {isValidDate(row['日付']) ? 
                    (new Date(row['日付']).getDay() === 0 || new Date(row['日付']).getDay() === 6 ? '休日' : '平日') : 
                    '不明'
                  }
                </div>
              </div>
              <div className="card-badges">
                <div className="users-badge">
                  <span className="badge-label">利用者</span>
                  <span className="badge-value">{getTotalUsers(row)}名</span>
                </div>
                <div className="interviews-badge">
                  <span className="badge-label">面談</span>
                  <span className="badge-value">{getTotalInterviews(row)}件</span>
                </div>
                
              </div>
            </div>
            
            <div className="card-body">
              <div className="users-section">
                <div className="section-title">利用者情報</div>
                <div className="users-grid">
                  <div className="user-item">
                    <span className="label">A型:</span>
                    <span className="value">{row['本日利用者A型'] || '0'}名</span>
                  </div>
                  <div className="user-item">
                    <span className="label">B型:</span>
                    <span className="value">{row['本日利用者B型'] || '0'}名</span>
                  </div>
                  <div className="user-item">
                    <span className="label">移行:</span>
                    <span className="value">{row['本日利用者移行'] || '0'}名</span>
                  </div>
                </div>
              </div>
              
              <div className="interviews-section">
                <div className="section-title">面談情報</div>
                <div className="interviews-grid">
                  <div className="interview-item">
                    <span className="label">新規面談A型:</span>
                    <span className="value">{row['本日新規面談A型'] || '0'}件</span>
                  </div>
                  <div className="interview-item">
                    <span className="label">新規面談B型:</span>
                    <span className="value">{row['本日新規面談B型'] || '0'}件</span>
                  </div>
                  <div className="interview-item">
                    <span className="label">新規面談移行:</span>
                    <span className="value">{row['本日新規面談移行'] || '0'}件</span>
                  </div>
                </div>
              </div>
              
              
              
              {row['特記事項'] && (
                <div className="notes-section">
                  <div className="section-title">特記事項</div>
                  <p className="notes-text">{row['特記事項']}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredData.length === 0 && (
        <div className="no-data">
          <p>選択された条件に該当するデータがありません。</p>
        </div>
      )}

      {/* 詳細表示モーダル */}
      {showDetail && selectedRowData && (
        <div className="detail-modal-overlay" onClick={handleCloseDetail}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{formatDate(selectedRowData['日付'])} の詳細</h3>
              <button className="close-button" onClick={handleCloseDetail}>×</button>
            </div>
            
            <div className="modal-content">
              <div className="detail-section">
                <h4>基本情報</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">日付:</span>
                    <span className="value">{formatDate(selectedRowData['日付'])}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">総利用者数:</span>
                    <span className="value">{getTotalUsers(selectedRowData)}名</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">総面談数:</span>
                    <span className="value">{getTotalInterviews(selectedRowData)}件</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>利用者詳細</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">本日利用者A型:</span>
                    <span className="value">{selectedRowData['本日利用者A型'] || '0'}名</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">本日利用者B型:</span>
                    <span className="value">{selectedRowData['本日利用者B型'] || '0'}名</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">本日利用者移行:</span>
                    <span className="value">{selectedRowData['本日利用者移行'] || '0'}名</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>面談詳細</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">本日新規面談A型:</span>
                    <span className="value">{selectedRowData['本日新規面談A型'] || '0'}件</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">本日新規面談B型:</span>
                    <span className="value">{selectedRowData['本日新規面談B型'] || '0'}件</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">本日新規面談移行:</span>
                    <span className="value">{selectedRowData['本日新規面談移行'] || '0'}件</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>翌営業日予定</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">来所者予定A型:</span>
                    <span className="value">{selectedRowData['翌営業日来所者予定A型'] || '0'}名</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">来所者予定B型:</span>
                    <span className="value">{selectedRowData['翌営業日来所者予定B型'] || '0'}名</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">来所者予定移行:</span>
                    <span className="value">{selectedRowData['翌営業日来所者予定移行'] || '0'}名</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">翌営業日の休み予定:</span>
                    <span className="value">{selectedRowData['翌営業日の休み予定'] || 'なし'}</span>
                  </div>
                </div>
              </div>

              

              {/* 関連する子データの表示 */}
              {childData.length > 0 && (
                <div className="detail-section">
                  <h4>関連面談スケジュール</h4>
                  <div className="child-data-table">
                    {getRelatedChildData(selectedRowData['日付']).length > 0 ? (
                      <table>
                        <thead>
                          <tr>
                            <th>氏名</th>
                            <th>予定時間</th>
                            <th>部類</th>
                            <th>内容</th>
                            <th>担当</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getRelatedChildData(selectedRowData['日付']).map((childRow, childIndex) => (
                            <tr key={childIndex}>
                              <td>{childRow['氏名'] || '-'}</td>
                              <td>{childRow['予定時間'] || '-'}</td>
                              <td>{childRow['部類'] || '-'}</td>
                              <td>{childRow['内容'] || '-'}</td>
                              <td>{childRow['担当'] || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p>この日付の面談スケジュールはありません。</p>
                    )}
                  </div>
                </div>
              )}

              {/* 関連する見学体験データの表示 */}
              {visitExperienceData.length > 0 && (
                <div className="detail-section">
                  <h4>関連見学体験</h4>
                  <div className="child-data-table">
                    {getRelatedVisitExperienceData(selectedRowData['日付']).length > 0 ? (
                      <table>
                        <thead>
                          <tr>
                            <th>氏名</th>
                            <th>時間</th>
                            <th>部類</th>
                            <th>内容</th>
                            <th>担当</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getRelatedVisitExperienceData(selectedRowData['日付']).map((visitRow, visitIndex) => (
                            <tr key={visitIndex}>
                              <td>{visitRow['氏名'] || '-'}</td>
                              <td>{visitRow['時間'] || '-'}</td>
                              <td>{visitRow['部類'] || '-'}</td>
                              <td>{visitRow['内容'] || '-'}</td>
                              <td>{visitRow['担当'] || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p>この日付の見学体験はありません。</p>
                    )}
                  </div>
                </div>
              )}

              {/* 関連する外出予定データの表示 */}
              {outingScheduleData.length > 0 && (
                <div className="detail-section">
                  <h4>関連外出予定</h4>
                  <div className="child-data-table">
                    {getRelatedOutingScheduleData(selectedRowData['日付']).length > 0 ? (
                      <table>
                        <thead>
                          <tr>
                            <th>氏名</th>
                            <th>予定時間</th>
                            <th>場所</th>
                            <th>用件</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getRelatedOutingScheduleData(selectedRowData['日付']).map((outingRow, outingIndex) => (
                            <tr key={outingIndex}>
                              <td>{outingRow['氏名'] || '-'}</td>
                              <td>{outingRow['予定時間'] || '-'}</td>
                              <td>{outingRow['場所'] || '-'}</td>
                              <td>{outingRow['用件'] || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p>この日付の外出予定はありません。</p>
                    )}
                  </div>
                </div>
              )}

              {selectedRowData['特記事項'] && (
                <div className="detail-section">
                  <h4>特記事項</h4>
                  <div className="notes-detail">
                    <p>{selectedRowData['特記事項']}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="log-footer">
        <div className="footer-stats">
          <span>📊 総レコード数: {parentData.length}</span>
          <span>🔍 表示中: {filteredData.length}</span>
          <span>📅 期間: {parentData.length > 0 ? 
            (() => {
              const validDates = parentData.filter(row => isValidDate(row['日付']));
              if (validDates.length > 0) {
                return `${formatDate(validDates[validDates.length - 1]['日付'])} 〜 ${formatDate(validDates[0]['日付'])}`;
              }
              return '有効な日付なし';
            })() : 'データなし'
          }</span>
        </div>
      </div>
    </div>
  );
};

export default BusinessDailyLogViewer;